import { extractMoneyCandidates } from "../money-parser.js";
import type { AiCapabilityPage } from "../registry/schema.js";
import { sameListedWord, wordsOf, type Word } from "../resolvers/vi-words.js";
import { confidenceToBps, type AiInterpretRejectReason } from "./confidence-policy.js";

/**
 * 🔴 UX-LOW-CONF-WRITE (W-2 lô GUIDE-UX3 16/10 · @UC0171 @UC0206 · nghiệm thu LLM thật N-2) — câu TRẦN "đợt thu" không được thành việc MỞ ĐỢT MỚI.
 *
 * ## Lỗi (đo, không đoán — B0 16/10 trên thế giới L2, LLM thật)
 * yen (thủ quỹ W9) gõ *"đợt thu"* ⇒ Người phiên dịch chọn `COL-EQUAL` độ chắc 0,35 (nghiệm thu: 0,2) ⇒ «Mỗi người đóng bao nhiêu?», gõ lại ⇒ «Mình chưa
 * đọc được số tiền "đợt thu"…» (vòng lặp). thao (W8, nhóm CHỈ có quỹ khoá) cùng câu ⇒ phiếu mở ⇒ ô quỹ rỗng `SCOPE_FUND_FROZEN` ⇒ «Quỹ đang bị đóng
 * băng nên mình chưa làm tiếp được việc này.» 0 nút (2/2). Cùng lượt đo: *"dot thu"* / *"xem đợt thu"* ⇒ READ-COLLECTIONS. Ngưỡng độ chắc chung của
 * chính sách (`AI_INTERPRET_MIN_CONFIDENCE_BPS_DEFAULT`) = 0 = TẮT có chủ đích (§3.8 — ma sát mới mặc định tắt) ⇒ độ chắc 0,35 vẫn `run`.
 *
 * ## Vì sao độ chắc MỘT MÌNH không đủ (cũng đo)
 * Câu có động từ mở rõ ràng LLM cũng khai thấp: *"mở đợt thu"* 0,42–0,55 · *"mở đợt thu mới"* 0,45–0,55; câu đủ nội dung *"tạo đợt thu 200k mỗi người"*
 * 0,86–0,9. Hạ ngưỡng chung sẽ bắt oan cả hai câu đầu (thêm một chạm cho người nói RÕ). Nên lưới đòi CẢ HAI bằng chứng:
 *  1. LLM tự khai chưa chắc: dưới `AI_LOW_CONFIDENCE_WRITE_BPS` (50% — dưới mức này chính LLM nói nó sai nhiều hơn đúng);
 *  2. câu KHÔNG có dấu hiệu việc GHI: không một động từ của tập ĐÓNG `WRITE_ACTION_VERBS`, không một cụm tiền (`extractMoneyCandidates` — bộ đọc tiền
 *     DUY NHẤT; số mơ hồ "2 trăm" cũng tính là có tiền ⇒ đường cũ, trang hỏi lại số như @UC0162).
 *
 * ## Đích: HỎI, không mở (0 phiếu, 0 ghi)
 * `{ read, write }` ⇒ Người làm hỏi "xem đợt thu hay mở đợt mới" với hai nút — trang ĐỌC đợt thu (`READ-COLLECTIONS`) + CHÍNH trang LLM đã chọn — qua
 * cửa nút (`GuideButtonGate.doerButtons`: cổng quyền + `ready()`); câu chỉ hứa nút mà cửa trả. Bấm nút = đường nút thường (0 LLM, thẻ xác nhận nếu ghi).
 *
 * ## Lệch luật chung có địa chỉ (ghi ở dòng `AI_DETERMINISTIC_NETS`)
 *  - Luật 1 ("CẢ câu thuộc khuôn đóng"): lưới không MỞ trang nào — nó HẠ một lựa chọn GHI chưa chắc thành câu hỏi. Chiều sai là một chạm thêm (người
 *    muốn mở đợt bấm nút «Mở đợt thu đều»), không phải một việc ghi; khuôn đóng của câu không cần — chỉ cần tập đóng động từ ghi VẮNG mặt.
 *  - Luật 4 (phủ định/nghi vấn ⇒ không làm gì): KHÔNG áp — câu hỏi "đợt thu?" càng không phải lệnh mở đợt; vì lưới chỉ hạ về câu hỏi, phủ định/nghi
 *    vấn làm lưới THÊM lý do chạy, không bớt.
 *  - Review W-2 MEDIUM-2 — câu có KỲ mà không động từ ("đợt thu tháng 10"): lưới HỎI, nên lưới kỳ (`collectionPeriodPageFromSentence`, chạy SAU) không điền
 *    tên/hạn; người bấm «Mở đợt thu đều» mở phiếu TRỐNG (nút không mang câu) và trang hỏi lại hạn theo luật cũ. Có chủ đích: cụm danh từ + kỳ là câu ĐỌC
 *    nhiều hơn ("đợt thu tháng 10 tới đâu rồi"); LLM thật 16/10 cũng chọn READ-COLLECTIONS cho chính câu này. Cái MẤT: người muốn mở đợt tháng 10 gõ
 *    thêm kỳ một lần — không ghi sai kỳ nào.
 * Phạm vi: chỉ họ trang MỞ đợt thu (trang khai `collectionFromSentence` — tự khám phá từ Sổ, không danh sách tay). Trang ghi khác chưa có trang đọc đối
 * ứng đã đặt tên ⇒ ngoài lưới (nợ, ghi ở hồ sơ PR). Thuần, 0 LLM, 0 I/O.
 */

/** Dưới mức này (basis-points, §3.1) LLM tự khai chưa chắc. 50% = chính lời khai nghiêng về SAI. Đo 16/10: "đợt thu" 0,2–0,35. */
export const AI_LOW_CONFIDENCE_WRITE_BPS = 5000;

/** Trang ĐỌC các đợt thu — vế "xem" của câu hỏi. Id ổn định của Sổ (`validateAiRegistry` đỏ nếu trang biến mất — test domain đọc qua `findAiPage`). */
export const COLLECTION_READ_PAGE_ID = "READ-COLLECTIONS";

/**
 * Động từ việc GHI — tập ĐÓNG, viết đúng chính tả có dấu. So bằng `sameListedWord`: chữ của câu gõ CÓ dấu phải trùng hình + dấu thanh ("thư"/"thứ" ≠ "thu"),
 * gõ KHÔNG dấu thì so mặt bỏ dấu ("mo" ≡ "mở" — và cả "mơ": nhận nhầm là động từ ⇒ lưới không chạy ⇒ đường cũ — chiều sai an toàn).
 * Bao trùm rộng có chủ đích: thừa một động từ chỉ làm lưới im (hành vi trước task), thiếu một động từ mới làm câu lệnh rõ bị hỏi lại.
 * Gồm: mở/tạo/lập/đặt/khai/phát động/setup đợt · thu/đóng/nộp/góp/gom/quyên/chuyển/ck/trả/chi (tiền) · ghi/nhắc/đòi · thêm/làm/lên/bắt đầu/khởi/kêu gọi/tổ chức · sửa đợt
 * (huỷ/ngừng/dừng/xoá/sửa/đổi/dời/lùi/gia hạn/kéo dài) — câu sửa đợt có lưới riêng (`collectionExtendPageFromSentence`) chạy TRƯỚC.
 */
const WRITE_ACTION_VERBS: readonly (readonly string[])[] = [
  ["mở"], ["tạo"], ["lập"], ["thu"], ["đóng"], ["nộp"], ["góp"], ["gom"], ["quyên"], ["chuyển"], ["ck"], ["trả"], ["chi"],
  ["ghi"], ["nhắc"], ["đòi"], ["thêm"], ["làm"], ["lên"], ["khởi"], ["kêu"], ["bắt", "đầu"], ["tổ", "chức"],
  ["huỷ"], ["ngừng"], ["dừng"], ["xoá"], ["sửa"], ["đổi"], ["dời"], ["lùi"], ["gia", "hạn"], ["kéo", "dài"],
  // review W-2 LOW-2: động từ khởi đợt khác người dùng hay gõ.
  ["đặt"], ["khai"], ["phát", "động"], ["setup"],
].map((v) => v.map((w) => w.normalize("NFC")));

/**
 * Chữ của tập động từ mà ở vị trí này là DANH TỪ, không phải lệnh: "thu" ngay sau đợt/khoản/lịch/phiếu/nguồn/kỳ/sổ ("đợt thu" = một đợt, "khoản thu"),
 * "chi" ngay trước "tiết" ("chi tiết"). Tập đóng; thiếu một cặp ⇒ chữ bị coi là động từ ⇒ lưới im (chiều sai an toàn).
 */
const NOUN_USES: readonly { readonly word: string; readonly before?: readonly string[]; readonly after?: readonly string[] }[] = [
  { word: "thu", before: ["đợt", "khoản", "lịch", "phiếu", "nguồn", "kỳ", "sổ"] },
  { word: "chi", after: ["tiết"] },
];

const VERB_WORDS: readonly (readonly Word[])[] = WRITE_ACTION_VERBS.map((v) => v.map((w) => wordsOf(w)[0]!));
const NOUN_USE_WORDS = NOUN_USES.map((n) => ({
  word: wordsOf(n.word)[0]!,
  before: (n.before ?? []).map((w) => wordsOf(w)[0]!),
  after: (n.after ?? []).map((w) => wordsOf(w)[0]!),
}));

function isNounUse(words: readonly Word[], i: number): boolean {
  const w = words[i]!;
  const prev = words[i - 1];
  const next = words[i + 1];
  return NOUN_USE_WORDS.some(
    (n) =>
      sameListedWord(w, n.word) &&
      ((prev !== undefined && n.before.some((b) => sameListedWord(prev, b))) || (next !== undefined && n.after.some((a) => sameListedWord(next, a)))),
  );
}

/** Câu có một động từ việc ghi của `WRITE_ACTION_VERBS` (trừ chỗ chữ đó là danh từ — `NOUN_USES`). Thuần. */
export function hasWriteActionVerb(sentence: string): boolean {
  const words = wordsOf(sentence);
  return words.some((_, i) => VERB_WORDS.some((v) => v.every((k, j) => words[i + j] !== undefined && sameListedWord(words[i + j]!, k)) && !(v.length === 1 && isNounUse(words, i))));
}

type ReadOrOpenPageLike = Pick<AiCapabilityPage, "id" | "collectionFromSentence">;

export interface CollectionReadOrOpenInput<P extends ReadOrOpenPageLike> {
  readonly sentence: string;
  /** Trang LLM đã chọn cho lượt này (chính sách đã cho `run`). */
  readonly page: P;
  /** Độ chắc LLM tự khai [0..1] cho trang đó — LỜI KHAI, đọc qua `confidenceToBps` (rác ⇒ 0 — fail-closed về phía HỎI). */
  readonly confidence: number;
  /** Trang ĐƯỢC PHÉP (cổng vai/cờ — @UC0152). */
  readonly allowed: readonly P[];
  readonly rejected: AiInterpretRejectReason | undefined;
  readonly simulated: boolean;
}

/**
 * LƯỚI (kind `page`, đích A/B) — xem docblock đầu file. `{ read, write }` ⇒ Người làm HỎI "xem đợt thu hay mở đợt mới" (không mở phiếu); `null` ⇒ đường
 * cũ. Luật chung 3: LLM không chạy thật ⇒ `null`. Trang đọc đợt thu ngoài `allowed` ⇒ `null` (không mời nút cửa khoá).
 */
export function collectionReadOrOpenFromSentence<P extends ReadOrOpenPageLike>(input: CollectionReadOrOpenInput<P>): { readonly read: P; readonly write: P } | null {
  if (input.rejected !== undefined || input.simulated) return null;
  if (input.page.collectionFromSentence === undefined) return null;
  if (confidenceToBps(input.confidence) >= AI_LOW_CONFIDENCE_WRITE_BPS) return null;
  if (hasWriteActionVerb(input.sentence) || extractMoneyCandidates(input.sentence).kind !== "none") return null;
  const read = input.allowed.find((p) => p.id === COLLECTION_READ_PAGE_ID);
  return read === undefined ? null : { read, write: input.page };
}
